import { useCallback, useMemo, useRef, useState } from "react";
import { api } from "./api";
import { transformDescription } from "./friendly-labels";

// Global search for SP1 (spec §5.5): a client-side index of clients and
// pipelines, loaded on first focus and cached for a minute, plus a UUID
// shortcut to a run. SP3 swaps the index for a server-side record search
// behind the same SearchBox.

export interface SearchIndex {
  clients: Array<{ id: string; name: string }>;
  pipelines: Array<{
    id: string;
    name: string;
    transformId?: string | null;
    /**
     * The flow label as the UI renders it, resolved once at index-build time.
     *
     * Resolving it per keystroke instead would mean calling
     * `transformDescription()` without the registry list, which silently drops
     * back to the local map — so a transform whose label only exists in the
     * registry would be unsearchable by the words actually on screen, and its
     * result row would show a raw transform id.
     */
    flow?: string;
  }>;
}

export interface SearchResult {
  kind: "client" | "pipeline" | "run";
  id: string;
  label: string;
  meta?: string;
  to: string;
}

export interface SearchResults {
  run: SearchResult | null;
  clients: SearchResult[];
  pipelines: SearchResult[];
}

export type SearchStatus = "idle" | "loading" | "ready" | "failed";

export const MAX_PER_GROUP = 5;
export const CACHE_TTL_MS = 60_000;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Whether the query is itself a run id, which search offers to open directly. */
export function isUuid(text: string): boolean {
  return UUID_RE.test(text.trim());
}

/** Prefers the label resolved at load; falls back for an index built without one. */
function flowOf(p: SearchIndex["pipelines"][number]): string {
  return p.flow ?? transformDescription(p.transformId);
}

const EMPTY: SearchResults = { run: null, clients: [], pipelines: [] };
const EMPTY_INDEX: SearchIndex = { clients: [], pipelines: [] };

/**
 * Match a query against the loaded index: at most `MAX_PER_GROUP` clients and
 * pipelines, plus an "Open run" result when the query is a run id.
 *
 * Pipelines match on their name and on what they move, so "orders" finds a
 * pipeline named for its client. Pure — the hook owns loading and state.
 */
export function searchIndex(index: SearchIndex, query: string): SearchResults {
  const q = query.trim().toLowerCase();
  if (!q) return EMPTY;
  const run: SearchResult | null = isUuid(q)
    ? { kind: "run", id: q, label: `Open run ${q}`, to: `/activity/${q}` }
    : null;
  const clients = index.clients
    .filter((c) => c.name.toLowerCase().includes(q))
    .slice(0, MAX_PER_GROUP)
    .map<SearchResult>((c) => ({ kind: "client", id: c.id, label: c.name, meta: c.id.slice(0, 8), to: `/clients/${c.id}` }));
  const pipelines = index.pipelines
    .filter((p) => {
      const desc = flowOf(p).toLowerCase();
      return p.name.toLowerCase().includes(q) || desc.includes(q);
    })
    .slice(0, MAX_PER_GROUP)
    .map<SearchResult>((p) => ({
      kind: "pipeline",
      id: p.id,
      label: p.name,
      meta: flowOf(p),
      to: `/pipelines/${p.id}`,
    }));
  return { run, clients, pipelines };
}

let cache: { at: number; index: SearchIndex } | null = null;

/** Test hook: forget the cached index. */
export function resetSearchCache() {
  cache = null;
}

/**
 * The clients and pipelines to search over, cached for `CACHE_TTL_MS`.
 *
 * Transform descriptors come along so a registry-declared label resolves once
 * here rather than on every keystroke. Losing them is not worth failing search
 * over, so that one call falls back to the local map.
 */
async function loadIndex(): Promise<SearchIndex> {
  if (cache && Date.now() - cache.at < CACHE_TTL_MS) return cache.index;
  // Transforms come along so registry-declared labels resolve here rather than
  // at query time; see SearchIndex.flow. A failure to load them is not worth
  // failing search over — the label falls back to the local map.
  const [orgs, pipes, transforms] = await Promise.all([
    api.listOrganizations(),
    api.listPipelines(),
    api.listTransforms().catch(() => ({ transforms: [] })),
  ]);
  const descriptors = transforms.transforms ?? [];
  const index: SearchIndex = {
    clients: (orgs.organizations ?? []).map((o: { id: string; name: string }) => ({ id: o.id, name: o.name })),
    pipelines: (pipes.pipelines ?? []).map((p: { id: string; name: string; transformId?: string | null }) => ({
      id: p.id,
      name: p.name,
      transformId: p.transformId ?? null,
      flow: transformDescription(p.transformId, descriptors),
    })),
  };
  cache = { at: Date.now(), index };
  return index;
}

/**
 * Search state for the top bar: the query, the load status, and the results.
 *
 * `load()` is called on focus and is safe to call repeatedly — it collapses
 * concurrent calls and, once the index has loaded, keeps the status at "ready"
 * through a background refresh so the dropdown does not flash back to
 * "Loading" under the operator. A run id still offers "Open run" even when the
 * index never loaded at all.
 */
export function useGlobalSearch() {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<SearchStatus>("idle");
  const [index, setIndex] = useState<SearchIndex | null>(null);
  const inFlight = useRef(false);

  const load = useCallback(() => {
    if (inFlight.current) return;
    inFlight.current = true;
    // Once the index has loaded successfully, a later call (a re-focus, say)
    // may still hit the network — the cache in loadIndex() has gone stale —
    // but the dropdown should keep reading "ready", not flash back to
    // "loading", while that background refresh is in flight.
    setStatus((s) => (s === "ready" ? s : "loading"));
    loadIndex()
      .then((i) => {
        setIndex(i);
        setStatus("ready");
      })
      .catch(() => setStatus("failed"))
      .finally(() => {
        inFlight.current = false;
      });
  }, []);

  // A UUID still offers "Open run" even when the index never loaded.
  const results = useMemo(() => searchIndex(index ?? EMPTY_INDEX, query), [index, query]);

  return { query, setQuery, status, results, load };
}
